/**
 * The blocks the Reels agent streams into the DANI chat, and the links on them.
 * Client-safe: types and pure helpers only.
 *
 * Every number on a block (a percentage, a base, a count) is copied from a tool
 * result and rendered from this data. The model writes none of them (PRD P0-7).
 */

import type { SynthesisResponse } from '@/lib/reel-studio/types';

/** The video study a conversation is about: the scope every Reels tool call is checked against. */
export interface VideoStudy {
    /** `sfProjectId`, the portal's `[projectId]` route param. */
    projectId: string;
    projectName: string;
    surveyIds: string[];
    /** The video question, once one is chosen. */
    questionId?: string;
    questionText?: string;
}

/** A study with its video question chosen. */
export interface VideoQuestionScope extends VideoStudy {
    questionId: string;
    questionText: string;
}

/** One playable clip. Its media URLs are same-origin proxy URLs, never raw S3 links. */
export interface ClipCard {
    clipId: string;
    panelistId: string;
    surveyId: string;
    /** The words shown with the clip, or null when the transcript is locked or missing. */
    quote: string | null;
    /** Where the quoted words start and end in the video, in seconds, when known. */
    startSec: number | null;
    endSec: number | null;
    videoUrl: string;
    thumbnailUrl: string | null;
    /** "Place · City, ST" on in-store surveys. */
    store: string | null;
}

export interface VideoProgressBlock {
    scope: VideoQuestionScope;
    analysisRunId: string | null;
    /** Videos collected for the question, when the multimedia API reported it. */
    collected: number | null;
    /** Transcribed responses the run reads. */
    transcribed: number | null;
    status: 'processing' | 'complete' | 'empty';
}

export interface VideoThemeCard {
    id: string;
    /** Written by the analysis model, not by a person: the card says so. */
    label: string;
    mentions: number;
    pct: number;
    base: number;
    pos: number;
    neu: number;
    neg: number;
    color: string;
    soft: string;
    segs: { label: string; pct: number }[];
    /** Up to a few example clips behind the theme. */
    clips: ClipCard[];
}

export interface VideoThemesBlock {
    scope: VideoQuestionScope;
    analysisRunId: string | null;
    summary: string;
    stats: SynthesisResponse['stats'];
    untranscribedCount: number | null;
    generatedAt: string | null;
    themes: VideoThemeCard[];
}

export interface ClipBlock {
    scope: VideoQuestionScope;
    /** What the clips answer: a search, a theme or a pick from earlier results. */
    kind: 'search' | 'theme' | 'selection';
    /** The searched phrase, for `search`. */
    query?: string;
    /** The theme label, for `theme`. */
    themeLabel?: string;
    /** For `search`: every matching moment, not only the clips shown. */
    matchCount?: number;
    /** For `search`: clips whose transcript is locked and so could not be searched. */
    lockedCount?: number;
    /** For `search`: videos collected for the question, when the multimedia API reported it. */
    collectedCount?: number;
    /** For `search`: transcripts that exist for the question, locked or not. */
    transcribedCount?: number;
    /**
     * For `search`: the question's analysis counts transcripts but none could be read, so
     * nothing was searched. Different from "not transcribed yet" (M4S-6043).
     */
    transcriptsUnavailable?: boolean;
    clips: ClipCard[];
}

/** The SSE event names the Reels agent adds to the chat's stream. */
export const REELS_BLOCK_EVENTS = {
    VIDEO_PROGRESS: 'video_progress',
    VIDEO_THEMES: 'video_themes',
    CLIP: 'clip',
    /** Not a block: tells the chat which study the conversation is now about. */
    VIDEO_STUDY: 'video_study',
} as const;

export type ReelsBlock =
    | { type: typeof REELS_BLOCK_EVENTS.VIDEO_PROGRESS; video_progress: VideoProgressBlock }
    | { type: typeof REELS_BLOCK_EVENTS.VIDEO_THEMES; video_themes: VideoThemesBlock }
    | { type: typeof REELS_BLOCK_EVENTS.CLIP; clip: ClipBlock };

export type ReelStudioTab = 'synthesis' | 'transcripts' | 'reel' | 'shareout';

/** "Open in Reel Studio": the Multimedia tab's video workspace on the same question. */
export function reelStudioHref(scope: VideoStudy, tab: ReelStudioTab = 'synthesis'): string {
    const params = new URLSearchParams({ tab });
    if (scope.questionId) params.set('q', scope.questionId);
    return `/portal/${encodeURIComponent(scope.projectId)}/analyze/videos?${params.toString()}`;
}
